import {useEffect, useMemo, useRef} from "react";
import {Box, Typography} from "@mui/material";
import {FabricObject} from "fabric";
import {useTranslation} from "react-i18next";
import {useFloorplanContext} from "../hooks/useFloorplanContext";
import {useReadStore} from "../hooks/useReadStore";
import {
    buildTableGroupOverlays,
    getTableGroupPadding,
    getTableGroupMemberId,
    StoredTableGroup,
    TableGroupKind,
    TableOverlayLike,
} from "../utils/table-groups";

interface Props {
    groups: StoredTableGroup[];
}

const GROUP_STYLES: Record<TableGroupKind, {
    border: string;
    background: string;
    labelBg: string;
    labelColor: string
}> = {
    combination: {
        border: "rgba(25, 118, 210, 0.4)",
        background: "transparent",
        labelBg: "rgba(250, 250, 250, 0.96)",
        labelColor: "rgba(25, 118, 210, 0.4)",
    },
    notBookable: {
        border: "rgba(211, 47, 47, 0.4)",
        background: "transparent",
        labelBg: "rgba(250, 250, 250, 0.96)",
        labelColor: "rgba(211, 47, 47, 0.4)",
    },
};

const buildTableTargets = (objects: FabricObject[]): TableOverlayLike[] => {
    return objects
        .map((obj) => {
            const memberId = getTableGroupMemberId(obj.floorPlanItem);
            if (!memberId) return null;

            const bounds = obj.getBoundingRect();
            return {
                id: memberId,
                x: bounds.left,
                y: bounds.top,
                width: bounds.width,
                height: bounds.height,
            };
        })
        .filter((target): target is TableOverlayLike => target !== null);
};

const TableGroupsOverlay: React.FC<Props> = ({groups}) => {
    const {t} = useTranslation();
    const {canvas, showSeatsAroundTable} = useFloorplanContext();
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const floorplanTables = useReadStore((state) => state.floorplanTables);

    const overlays = useMemo(() => {
        if (!canvas || groups.length === 0 || floorplanTables.length === 0) return [];

        const tableTargets = buildTableTargets(
            floorplanTables
                .map((table) => table.fabricObject)
                .filter((obj): obj is FabricObject => !!obj?.floorPlanItem?.table),
        );

        return buildTableGroupOverlays(groups, tableTargets, getTableGroupPadding(showSeatsAroundTable));
    }, [canvas, floorplanTables, groups, showSeatsAroundTable]);

    useEffect(() => {
        if (!canvas) return;

        const syncTransform = () => {
            const wrapper = wrapperRef.current;
            const viewportTransform = canvas.viewportTransform;
            if (!wrapper || !viewportTransform) return;
            wrapper.style.transform = `matrix(${viewportTransform[0]}, 0, 0, ${viewportTransform[3]}, ${viewportTransform[4]}, ${viewportTransform[5]})`;
        };

        syncTransform();
        canvas.on("after:render", syncTransform);
        return () => {
            canvas.off("after:render", syncTransform);
        };
    }, [canvas]);

    if (overlays.length === 0) return null;

    return (
        <div
            ref={wrapperRef}
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: 0,
                height: 0,
                transformOrigin: "0 0",
                pointerEvents: "none",
                zIndex: 2,
            }}
        >
            {overlays.map((overlay) => {
                const style = GROUP_STYLES[overlay.kind];

                return (
                    <Box
                        key={overlay.id}
                        sx={{
                            position: "absolute",
                            left: overlay.x,
                            top: overlay.y,
                            width: overlay.width,
                            height: overlay.height,
                            border: `2px dashed ${style.border}`,
                            background: style.background,
                            borderRadius: 4,
                            boxSizing: "border-box",
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                top: -2,
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                px: 0.75,
                                py: 0.2,
                                borderRadius: 1,
                                bgcolor: style.labelBg,
                                border: `1px solid ${style.border}`,
                                zIndex: 2,
                                whiteSpace: "nowrap",
                                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.08)",
                            }}
                        >
                            <Typography variant="caption" sx={{fontWeight: 600, color: style.labelColor}}>
                                {t(overlay.label, {defaultValue: overlay.label})}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </div>
    );
};

export default TableGroupsOverlay;
